import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaneControls, PaneGroup, PanesMenu, usePaneLayout } from '../components/Panes';
import { FilePlus2, Library as LibraryIcon, Search, SlidersHorizontal, Upload } from 'lucide-react';
import { useCategories, useLibrary, useMe, useSchema, useStorage, useStrata } from '../api/hooks';
import { pickFiles } from '../components/DropZone';
import { isTyping } from '../lib/keys';
import { NewPlaceholderDialog } from '../components/NewPlaceholder';
import { useLibraryColumns } from './LibraryColumns';
import { AdvancedSearchPanel, usableAdvanced } from './AdvancedSearch';
import { useLibraryFilters } from './library/useLibraryFilters';
import { FiltersPane, ResultsPane } from './library/LibraryPanes';
import { LibraryInspector, SelectionSummary } from './library/LibraryInspector';
import { useLibrarySelection, type SelectGesture } from './library/useLibrarySelection';
import { useLoadMore } from './library/LibraryItems';
import { SaveSearchDialog } from './library/SavedSearches';
import type { AssetSummary } from '@prism/contracts';
import { GroupSearchSuggestions } from './library/group-filter/GroupFilter';
import { GROUPED_READY } from './library/grouped/GroupedView';

export function Library() {
  const me = useMe().data!;
  const schema = useSchema().data?.definition;
  const cols = useLibraryColumns(schema);
  const locations = useStorage().data?.items ?? [];
  const categories = useCategories().data?.items ?? [];
  const navigate = useNavigate();
  const f = useLibraryFilters(schema);
  const { view, setView, search, setSearch, advanced, showAdvanced, setShowAdvanced, clearAll } = f;
  const strata = useStrata().data?.items ?? [];
  const [newPlaceholder, setNewPlaceholder] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const canUpload = me.permissions.includes('asset.upload');

  // 1.4.0 (G3): in the Grouped view the selection, the keys and the Inspector work on the asset rows on screen, and
  // the flat list is not asked for (the tree pages each level itself).
  const [groupedItems, setGroupedItems] = useState<AssetSummary[]>([]);
  const groupedView = GROUPED_READY && view === 'table' && f.grouped;
  const query = useLibrary({ ...f.activeFilters, q: f.q, sort: f.sort, advanced: f.advancedQuery, group: f.group }, { enabled: !f.waitingForModel && !groupedView });
  const flatItems = query.data?.items ?? [];
  const items = groupedView ? groupedItems : flatItems;
  const loadMore = useLoadMore(query);
  const selected = items.find((a) => a.id === selectedId) ?? null;
  // Multi-select (1.2.0): cleared when the search or the filters change (not the sort or the view).
  const resetKey = useMemo(() => JSON.stringify([f.filters, f.q, f.advancedQuery, f.group]), [f.filters, f.q, f.advancedQuery, f.group]);
  const selection = useLibrarySelection(resetKey);
  const chosen = selection.selectedOf(items);
  const order = useMemo(() => items.map((a) => a.id), [items]);

  useEffect(() => {
    // Select the first result when nothing (or an asset that left the list, e.g. deleted) is selected.
    if (items[0] && !items.some((a) => a.id === selectedId)) setSelectedId(items[0].id);
  }, [items, selectedId]);

  /** A click on a card or row. A Cmd/Ctrl- or Shift-click with nothing selected yet starts from the Inspector's asset. */
  const onSelect = useCallback((id: string, g: SelectGesture) => {
    if ((g.shiftKey || g.metaKey || g.ctrlKey) && !selection.count && selectedId && selectedId !== id) selection.click(selectedId, {}, order);
    selection.click(id, g, order);
    setSelectedId(id);
  }, [selection, selectedId, order]);
  const onCheck = useCallback((id: string, g: SelectGesture) => {
    selection.click(id, g, order);
  }, [selection, order]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !isTyping(e)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key.toLowerCase() === 'g' && !isTyping(e) && !e.metaKey && !e.ctrlKey) setView(view === 'grid' ? 'table' : 'grid');
      // Not while a dialog or popover is open (it has its own keys).
      // A selection checkbox is not a field: Cmd-A and Escape still apply.
      if ((isTyping(e) && !isTyping(e, '.select-check')) || document.querySelector('[role="dialog"]')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a' && order.length) {
        e.preventDefault();
        selection.selectAll(order);
      }
      // Escape clears the selection first; the list's own Escape (onListKey) clears the search and filters after.
      if (e.key === 'Escape' && selection.count >= 2) selection.clear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, setView, order, selection]);

  const onListKey = useCallback(
    (e: ReactKeyboardEvent) => {
      if (!items.length) return;
      const idx = Math.max(0, items.findIndex((a) => a.id === selectedId));
      let cols = 1;
      if (view === 'grid' && gridRef.current) {
        const tpl = getComputedStyle(gridRef.current).gridTemplateColumns.split(' ').length;
        cols = Math.max(1, tpl);
      }
      const move = (n: number) => {
        e.preventDefault();
        const next = items[Math.min(items.length - 1, Math.max(0, idx + n))]!;
        // Shift extends the selection from its anchor; a plain arrow moves a selection to the next one only.
        if (e.shiftKey) onSelect(next.id, { shiftKey: true });
        else if (selection.count) selection.click(next.id, {}, order);
        setSelectedId(next.id);
        document.querySelector<HTMLElement>(`[data-asset-id="${next.id}"]`)?.focus();
      };
      if (e.key === 'ArrowRight' && view === 'grid') move(1);
      else if (e.key === 'ArrowLeft' && view === 'grid') move(-1);
      else if (e.key === 'ArrowDown') move(cols);
      else if (e.key === 'ArrowUp') move(-cols);
      else if (e.key === 'Enter' && selectedId) navigate(`/assets/${selectedId}`);
      else if (e.key === 'Escape' && selection.count < 2) clearAll();
    },
    [items, selectedId, view, navigate, selection, order, onSelect],
  );

  const panes = usePaneLayout('library', {
    main: [
      { id: 'facets', label: 'Filters', defaultSize: '17%', minSize: '170px', maxSize: '340px', render: () => (
        <FiltersPane f={f} listVersion={groupedView ? 0 : query.isPlaceholderData ? undefined : query.data?.version} schema={schema} categories={categories} locations={locations} canDelete={me.permissions.includes('asset.delete')} />
      ) },
      { id: 'results', label: 'Assets', required: true, minSize: '360px', render: () => (
        <ResultsPane f={f} query={query} schema={schema} strata={strata} categories={categories} locations={locations} cols={cols}
          focusedId={selectedId} selection={selection} onSelect={onSelect} onFocusItem={setSelectedId} onCheck={onCheck} onOpen={(id) => navigate(`/assets/${id}`)}
          onMoment={(id, seconds, subtitle) => navigate(`/assets/${id}?t=${seconds.toFixed(3)}${subtitle ? '&tab=subtitles' : ''}`)}
          onSaveSearch={() => setSavingSearch(true)} onListKey={onListKey} gridRef={gridRef} loadMore={loadMore} canUpload={canUpload}
          groupedView={groupedView} groupedItems={groupedItems} onVisibleItems={setGroupedItems} />
      ) },
      { id: 'inspector', label: 'Inspector', defaultSize: '25%', minSize: '260px', maxSize: '520px', render: () => (
            <aside className="pane raised" aria-label="Inspector">
              <div className="pane-header"><span className="pane-title">Inspector</span><div style={{ flex: 1 }} /><PaneControls /></div>
              <div className="pane-body">
                {chosen.length >= 2 ? <SelectionSummary assets={chosen} onClear={selection.clear} />
                  : (chosen[0] ?? selected) ? <LibraryInspector asset={(chosen[0] ?? selected)!} schema={schema} />
                  : <div className="empty"><p className="muted">Select an asset to see its details.</p></div>}
              </div>
            </aside>
      ) },
    ],
  });
  return (
    <>
      <header className="topbar">
        <h1><LibraryIcon size={16} color="var(--accent-strong)" /> Library</h1>
        <div className="search library-search" role="search">
          <Search size={15} />
          <input
            ref={searchRef}
            className="input"
            placeholder="Search titles, keywords, descriptions, filenames…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setSearch(''); searchRef.current?.blur(); }
              if (e.key === 'ArrowDown') { e.preventDefault(); document.querySelector<HTMLElement>('[data-asset-id]')?.focus(); }
            }}
            aria-label="Search the library"
            data-testid="library-search"
          />
          <kbd>/</kbd>
          <span className="search-help" role="note">Use * as a wildcard, e.g. interv*. ? matches one character.</span>
          {/* 1.4.0 (G1): "Series: American Horror Story" suggestions, which apply the group filter. */}
          <GroupSearchSuggestions q={search} onPick={(id) => { f.setGroup(id); setSearch(''); }} />
        </div>
        <button className={`btn ${showAdvanced || usableAdvanced(advanced) ? '' : 'ghost'} sm`} onClick={() => setShowAdvanced(!showAdvanced)} aria-pressed={showAdvanced} data-testid="advanced-toggle"
          title="Search in chosen fields and add conditions">
          <SlidersHorizontal size={14} /> Advanced{usableAdvanced(advanced) ? ` · ${(advanced.in.length ? 1 : 0) + advanced.rules.length}` : ''}
        </button>
        <div className="topbar-spacer" />
        <PanesMenu layout={panes} />
        {canUpload && (
          <button className="btn" onClick={() => setNewPlaceholder(true)} data-testid="new-placeholder" title="Create an asset without media; add the media later">
            <FilePlus2 size={15} /> New placeholder…
          </button>
        )}
        {canUpload && (
          <button className="btn primary" onClick={pickFiles} data-testid="upload-button">
            <Upload size={15} /> Upload
          </button>
        )}
      </header>
      {newPlaceholder && <NewPlaceholderDialog onClose={() => setNewPlaceholder(false)} />}
      {savingSearch && <SaveSearchDialog query={f.currentQuery()} onClose={() => setSavingSearch(false)} />}

      {showAdvanced && <AdvancedSearchPanel value={advanced} onChange={f.setAdvanced} schema={schema} strata={strata} />}
      <div className="content">
        <PaneGroup layout={panes} row="main" />
      </div>
    </>
  );
}
